import { useSyncExternalStore, type CSSProperties, type JSX } from 'react';

/**
 * The mahjong tile face, drawn to the component sheet (design doc §1c).
 *
 * Every number on this page is lifted from the doc's own `DL`/`SL` tables
 * rather than eyeballed, because the pip compositions are the tile — five
 * balls are a quincunx, nine bamboo are a red cross through a green field, and
 * an auto-flowing grid gets all of that wrong while still counting correctly.
 *
 * Two faces, not one, and the doc draws both: a rack tile has room for the art,
 * a discard tile does not and gets a two-line abbreviation instead. Which one
 * you see is a question of size, and only of size.
 */

export type TileSize = 'full' | 'mini' | 'tiny';

/** Suit inks. `IC` in the doc; the fourth is the navy honors are drawn in. */
const B = '#2c4e86';
const R = '#b93a26';
const G = '#1e7f55';
const NV = '#233049';
const IC: Record<string, string> = { b: B, r: R, g: G };

/**
 * Balls. `[x, y, ink, diameter, ring]`, x/y the CENTRE of each pip in the
 * doc's 56×76 art box — which sits inside the tile, not flush with it.
 */
export const DOTS: Record<string, [number, number, string, number, number][]> = {
  1: [[28, 36, 'r', 30, 3.5]],
  2: [
    [28, 17, 'b', 20, 2.5],
    [28, 55, 'g', 20, 2.5],
  ],
  3: [
    [14, 14, 'b', 18, 2.5],
    [28, 36, 'r', 18, 2.5],
    [42, 58, 'g', 18, 2.5],
  ],
  4: [
    [15, 16, 'b', 17, 2.5],
    [41, 16, 'g', 17, 2.5],
    [15, 56, 'g', 17, 2.5],
    [41, 56, 'b', 17, 2.5],
  ],
  5: [
    [13, 14, 'b', 15, 2.2],
    [43, 14, 'g', 15, 2.2],
    [28, 36, 'r', 15, 2.2],
    [13, 58, 'g', 15, 2.2],
    [43, 58, 'b', 15, 2.2],
  ],
  6: [
    [16, 14, 'g', 14, 2.2],
    [40, 14, 'g', 14, 2.2],
    [16, 36, 'r', 14, 2.2],
    [40, 36, 'r', 14, 2.2],
    [16, 58, 'r', 14, 2.2],
    [40, 58, 'r', 14, 2.2],
  ],
  7: [
    [11, 10, 'g', 12, 2],
    [28, 15, 'g', 12, 2],
    [45, 20, 'g', 12, 2],
    [16, 40, 'r', 12, 2],
    [40, 40, 'r', 12, 2],
    [16, 60, 'r', 12, 2],
    [40, 60, 'r', 12, 2],
  ],
  8: [
    [16, 11, 'b', 12, 2],
    [40, 11, 'b', 12, 2],
    [16, 28, 'b', 12, 2],
    [40, 28, 'b', 12, 2],
    [16, 45, 'b', 12, 2],
    [40, 45, 'b', 12, 2],
    [16, 62, 'b', 12, 2],
    [40, 62, 'b', 12, 2],
  ],
  9: [
    [13, 13, 'b', 12, 2],
    [28, 13, 'b', 12, 2],
    [43, 13, 'b', 12, 2],
    [13, 36, 'r', 12, 2],
    [28, 36, 'r', 12, 2],
    [43, 36, 'r', 12, 2],
    [13, 59, 'g', 12, 2],
    [28, 59, 'g', 12, 2],
    [43, 59, 'g', 12, 2],
  ],
};

/** Bamboo. `[x, y, w, h, ink]`, same 56×76 box, same centre-origin rule. */
export const STICKS: Record<string, [number, number, number, number, string][]> = {
  1: [[28, 36, 13, 48, 'r']],
  2: [
    [28, 17, 9, 25, 'g'],
    [28, 55, 9, 25, 'g'],
  ],
  3: [
    [28, 15, 9, 23, 'g'],
    [15, 56, 9, 23, 'g'],
    [41, 56, 9, 23, 'g'],
  ],
  4: [
    [15, 17, 9, 25, 'g'],
    [41, 17, 9, 25, 'g'],
    [15, 55, 9, 25, 'g'],
    [41, 55, 9, 25, 'g'],
  ],
  5: [
    [13, 15, 8, 22, 'g'],
    [43, 15, 8, 22, 'g'],
    [28, 36, 8, 22, 'r'],
    [13, 57, 8, 22, 'g'],
    [43, 57, 8, 22, 'g'],
  ],
  6: [
    [13, 16, 8, 23, 'g'],
    [28, 16, 8, 23, 'g'],
    [43, 16, 8, 23, 'g'],
    [13, 56, 8, 23, 'g'],
    [28, 56, 8, 23, 'g'],
    [43, 56, 8, 23, 'g'],
  ],
  7: [
    [28, 11, 7, 18, 'r'],
    [13, 34, 7, 18, 'g'],
    [28, 34, 7, 18, 'g'],
    [43, 34, 7, 18, 'g'],
    [13, 58, 7, 18, 'g'],
    [28, 58, 7, 18, 'g'],
    [43, 58, 7, 18, 'g'],
  ],
  8: [
    [10, 16, 7, 23, 'g'],
    [22, 16, 7, 23, 'g'],
    [34, 16, 7, 23, 'g'],
    [46, 16, 7, 23, 'g'],
    [10, 56, 7, 23, 'g'],
    [22, 56, 7, 23, 'g'],
    [34, 56, 7, 23, 'g'],
    [46, 56, 7, 23, 'g'],
  ],
  9: [
    [13, 13, 7, 19, 'g'],
    [28, 13, 7, 19, 'r'],
    [43, 13, 7, 19, 'g'],
    [13, 36, 7, 19, 'r'],
    [28, 36, 7, 19, 'r'],
    [43, 36, 7, 19, 'r'],
    [13, 59, 7, 19, 'g'],
    [28, 59, 7, 19, 'g'],
    [43, 59, 7, 19, 'g'],
  ],
};

/** 一…九, indexed by rank. The 萬 below them is the same on every tile. */
const CJ = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];
export const WAN = '萬';

const WIND_GLYPH: Record<string, string> = { E: '東', S: '南', W: '西', N: '北' };
const DRAGON_GLYPH: Record<string, string> = { red: '中', green: '發' };
const DRAGON_INK: Record<string, string> = { red: R, green: G };

/**
 * Every glyph the tiles need, and nothing else — this string is the `text=`
 * subset request, so the download is these characters and not a CJK font.
 */
export const TILE_GLYPHS = CJ.join('') + WAN + '東南西北中發';

/**
 * Is the CJK face actually available on this device?
 *
 * The board's long-standing rule is that a tofu box on a tile is worse than no
 * mark at all, and that rule does not stop being true because the design doc
 * draws 萬. So the glyph faces are gated on the font having really arrived:
 * when it has not, every face falls back to the drawn numeral this board used
 * before, which is ugly next to the art but never unreadable.
 */
let glyphsReady = false;
let asked = false;
const watchers = new Set<() => void>();

/** Matches `--font-cjk` in tokens.css. Either source of 萬 is a real face. */
const CJK_STACK = "'Noto Serif TC', 'Songti TC', 'Noto Serif CJK TC', serif";

function askOnce(): void {
  if (asked) return;
  asked = true;
  const fonts = typeof document === 'undefined' ? undefined : document.fonts;
  if (!fonts) return;
  const settle = (): void => {
    glyphsReady = drawsGlyph(WAN);
    for (const w of watchers) w();
  };
  /*
   * `load()`, not `ready`. A `display=swap` webfont is not fetched until
   * something paints a glyph in it, and nothing outside this board ever does —
   * so `fonts.ready` resolves with the subset never requested, the answer
   * latches to "no", and the entire CJK face would be dead for the session on
   * a device that could render it perfectly well.
   */
  void fonts.load(`900 20px ${CJK_STACK}`, TILE_GLYPHS).then(settle, settle);
}

/**
 * Does 萬 actually draw?
 *
 * Not "did the webfont load" — a phone with its own CJK font is just as good,
 * and the subset may be blocked while the system face is right there. And not
 * `fonts.check` either: it answers true for a family the browser has never
 * heard of, because a generic fallback can always be "used". Both mistakes end
 * in tofu on a tile, which this board's rule forbids.
 *
 * Comparing widths does not work here either, and it is worth writing down why
 * because it looks like it should: a CJK .notdef box is full-width, and so is
 * 萬 — both measure exactly 1em, so the test passes on a font with no CJK in it
 * at all. Draw them instead and compare the pixels. Same picture, same box.
 */
function drawsGlyph(ch: string): boolean {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    const stamp = (s: string): string => {
      ctx.clearRect(0, 0, 64, 64);
      ctx.font = `900 44px ${CJK_STACK}`;
      ctx.textBaseline = 'top';
      ctx.fillText(s, 4, 4);
      return canvas.toDataURL();
    };
    const glyph = stamp(ch);
    // Nothing painted at all, or painted as the missing-glyph box.
    return glyph !== stamp(' ') && glyph !== stamp('￿');
  } catch {
    return false;
  }
}

export function useTileGlyphs(): boolean {
  // One font check for the whole board rather than one per tile: every tile on
  // screen subscribes to the same answer.
  return useSyncExternalStore(
    (onChange) => {
      watchers.add(onChange);
      askOnce();
      return () => void watchers.delete(onChange);
    },
    () => glyphsReady,
    () => false,
  );
}

/* ── The rack face: the art itself ────────────────────────────────────────── */

type ArtVars = CSSProperties & { '--x': number; '--y': number; '--w': number; '--bw'?: number };

function dots(value: string): JSX.Element | null {
  const layout = DOTS[value];
  if (!layout) return null;
  return (
    <span className="mj-art">
      {layout.map(([x, y, ink, w, bw], i) => (
        <i
          key={i}
          className="mj-art__dot"
          style={
            {
              '--x': x,
              '--y': y,
              '--w': w,
              '--bw': bw,
              // A ring with a concentric core, exactly as the doc draws it: the
              // pip reads as a ball rather than a blob at any size.
              borderColor: IC[ink],
              background: `radial-gradient(circle, ${IC[ink]} 0 34%, rgba(0,0,0,0) 36%)`,
            } as ArtVars
          }
        />
      ))}
    </span>
  );
}

function sticks(value: string): JSX.Element | null {
  const layout = STICKS[value];
  if (!layout) return null;
  return (
    <span className="mj-art">
      {layout.map(([x, y, w, h, ink], i) => (
        <i
          key={i}
          className="mj-art__stick"
          style={
            {
              '--x': x,
              '--y': y,
              '--w': w,
              '--h': h,
              // Three segments with two gaps — a length of bamboo, not a bar.
              background: `linear-gradient(180deg, ${IC[ink]} 0 30%, rgba(0,0,0,0) 30% 36%, ${IC[ink]} 36% 64%, rgba(0,0,0,0) 64% 70%, ${IC[ink]} 70% 100%)`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}

/**
 * Flowers. Four petals and a gold heart, built from five stacked radial
 * gradients on one element — the doc's `bloom()`. Drawn rather than typed on
 * purpose: the old face was a ✿ from whatever font the device had, which is
 * the same tofu risk the caracteres face was written to avoid.
 */
function bloom(petal: string): string {
  /*
   * The doc's radii are px on a 46×46 box, and they stay px here — scaled by
   * the same `--art` the box is, so the proportions hold at every size.
   *
   * Restating them as percentages is the obvious move and it is wrong: a
   * radial-gradient's stop percentages are fractions of the GRADIENT RAY, not
   * of the box. The ray runs to the farthest corner, which is 0.93 of a side
   * for a petal and only 0.71 for the centre — so a faithful-looking 14.1%
   * heart comes out at 71% of its radius, half its area, and the blossom falls
   * apart into four detached dots.
   */
  const r = (px: number): string => `calc(${px}px * var(--art))`;
  return [
    `radial-gradient(circle at 50% 21%, ${petal} 0 ${r(9)}, rgba(0,0,0,0) ${r(9.5)})`,
    `radial-gradient(circle at 79% 50%, ${petal} 0 ${r(9)}, rgba(0,0,0,0) ${r(9.5)})`,
    `radial-gradient(circle at 50% 79%, ${petal} 0 ${r(9)}, rgba(0,0,0,0) ${r(9.5)})`,
    `radial-gradient(circle at 21% 50%, ${petal} 0 ${r(9)}, rgba(0,0,0,0) ${r(9.5)})`,
    `radial-gradient(circle at 50% 50%, #c9a227 0 ${r(6.5)}, rgba(0,0,0,0) ${r(7)})`,
  ].join(',');
}

const FLOWER_INK = (value: string): string => (Number(value) <= 4 ? '#c7492d' : '#3e6fb0');

/* ── The discard face: an abbreviation ────────────────────────────────────── */

export interface Abbrev {
  /** The line that carries the value. */
  a: string;
  /** The suit mark under it, empty for an honor. */
  b: string;
  ink: string;
}

/**
 * What a tile says when it is too small to draw.
 *
 * This is the doc's own discard-pool tile: a value over a suit mark, two lines,
 * one ink. `●` and `∥` are geometry rather than language, so they carry with no
 * font behind them; the caracteres and honor lines need the CJK face and say so
 * by falling back to Latin when it is missing.
 */
export function abbrev(suit: string, value: string, glyphs: boolean): Abbrev {
  if (suit === 'dots') return { a: value, b: '●', ink: B };
  if (suit === 'bamboo') return { a: value, b: '∥', ink: G };
  if (suit === 'chars') {
    const cj = CJ[Number(value) - 1];
    // 'C' for Caracteres, the same initial the corner index already uses — a
    // Latin 'W' for 萬 would read as the West wind.
    return glyphs && cj ? { a: cj, b: WAN, ink: R } : { a: value, b: 'C', ink: R };
  }
  if (suit === 'wind') {
    const g = WIND_GLYPH[value];
    return { a: glyphs && g ? g : value, b: '', ink: NV };
  }
  if (suit === 'dragon') {
    const g = DRAGON_GLYPH[value];
    if (glyphs && g) return { a: g, b: '', ink: DRAGON_INK[value] ?? NV };
    if (value === 'white') return { a: '', b: '', ink: B };
    // R and G, not the doc's C and F: the corner index already spells the
    // dragons R·G·P, and one tile answering to two letters is worse than
    // differing from a mock nobody can see next to it. 'C' is Caracteres here.
    return { a: value === 'red' ? 'R' : 'G', b: '', ink: DRAGON_INK[value] ?? NV };
  }
  return { a: value, b: '', ink: FLOWER_INK(value) };
}

/* ── The face ─────────────────────────────────────────────────────────────── */

/**
 * `full` gets the art; anything smaller gets the abbreviation.
 *
 * That split is the doc's, not a compromise: it draws a 36×50 rack tile with
 * pips on it and a 26×36 discard tile with two lines of text on it, and never
 * once tries to fit nine countable balls into 26px.
 */
export function TileFace({
  suit,
  value,
  size,
  glyphs,
}: {
  suit: string;
  value: string;
  size: TileSize;
  glyphs: boolean;
}): JSX.Element {
  /*
   * The three numbered suits carry their real face at every size — the rack,
   * the pool, your melds, the hand you won with, and the melds across the
   * table. A discard is the same object that was in a hand a second ago, and
   * showing it as "3 over ●" made the river read as a list of results rather
   * than tiles lying on a table.
   *
   * `tiny` is 15×21 and the pips land at about 3px there, so a nine is a
   * texture rather than nine countable things. It is drawn anyway, on purpose:
   * across the table you are reading a meld's SHAPE — which suit, is it a pong
   * or a chow — far more often than you are counting its pips, and a row of
   * real faces answers that at a glance where a row of numerals does not.
   * `--art` is opened up for that size alone so the pips are as large as the
   * tile can hold; the rank in the corner is what carries the exact number,
   * and it stops at `mini` because 4px of numeral would be neither.
   */
  if (suit === 'dots') return dots(value) ?? <span className="mj-art" />;
  if (suit === 'bamboo') return sticks(value) ?? <span className="mj-art" />;
  if (suit === 'chars') {
    const cj = CJ[Number(value) - 1];
    if (!glyphs || !cj) return <Numeral value={value} />;
    return (
      <span className="mj-tile__wan">
        <span>{cj}</span>
        <span>{WAN}</span>
      </span>
    );
  }

  if (size === 'full') {
    if (suit === 'flower') {
      const ink = FLOWER_INK(value);
      return (
        <>
          <span className="mj-art">
            <i className="mj-art__bloom" style={{ background: bloom(ink) }} />
          </span>
          <b className="mj-art__fnum" style={{ color: ink }}>
            {Number(value) <= 4 ? value : String(Number(value) - 4)}
          </b>
        </>
      );
    }
    if (suit === 'dragon' && value === 'white') return <i className="mj-art__pai" />;
    // Winds and the two coloured dragons: one glyph, dead centre.
    const g = suit === 'wind' ? WIND_GLYPH[value] : DRAGON_GLYPH[value];
    if (!glyphs || !g) return <Legacy suit={suit} value={value} />;
    return (
      <span className="mj-tile__glyph" style={{ color: suit === 'wind' ? NV : DRAGON_INK[value] }}>
        {g}
      </span>
    );
  }

  if (suit === 'dragon' && value === 'white') return <i className="mj-art__pai" />;
  if (suit === 'flower') {
    const ink = FLOWER_INK(value);
    return (
      <span className="mj-art">
        <i className="mj-art__bloom" style={{ background: bloom(ink) }} />
      </span>
    );
  }

  const { a, b, ink } = abbrev(suit, value, glyphs);
  return (
    <span
      className="mj-tile__ab"
      style={{ color: ink }}
      data-cjk={glyphs && suit === 'chars'}
      data-solo={b === ''}
    >
      <b>{a}</b>
      {b ? <span>{b}</span> : null}
    </span>
  );
}

/** The pre-glyph face, kept for when the CJK subset has not arrived. */
function Numeral({ value }: { value: string }): JSX.Element {
  return (
    <span className="mj-tile__num">
      <b>{value}</b>
      <i className="mj-tile__mark" />
    </span>
  );
}

function Legacy({ suit, value }: { suit: string; value: string }): JSX.Element {
  if (suit === 'wind') return <span className="mj-tile__honor">{value}</span>;
  return <span className="mj-tile__dragon" data-d={value} />;
}
